import React, { useEffect, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import { useShallow } from "zustand/react/shallow";
import { ModelElementType } from "@/components/entities";
import { TooltipButton } from "components/common/TooltipButton";
import { EmptyState } from "@/components/common/EmptyState";
import { SectionRule } from "components/common/SectionRule";
import { StatusChip } from "@/components/common/StatusChip";
import { Tooltip } from "components/common/Tooltip";
import { resolveDatasetColor } from "@/lib/datasetColor";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { useSharedWithMeStore } from "@/components/main/stores/sharedWithMeStore";
import { spaceColor } from "@/components/main/stores/orgContextStore";
import { useModelsPolling } from "@/components/main/hooks/useModelsPolling";
import { pollingAllowed } from "@/components/hooks/activityGate";
import { ModelRow } from "@/components/main/overview/ModelRow";
import { HintRow } from "@/components/main/overview/ListEmptyShell";
import { ColumnTopSpacer } from "@/components/main/pages/pageColumn";
import { formatCompactRelativeTime } from "@/components/main/overview/selectors";
import {
  partitionSharedModels,
  roleLabel,
  selectExternalModels,
} from "@/components/main/sharedWithMe/selectors";
import type { SharedSpace } from "@/lib/api/sharedWithMe";

// Same cadence as the models poll, so both halves of the page age alike.
const SPACES_POLL_INTERVAL_MS = 30_000;

/// "Collaborator · Ines Vogt". `tooltip` only for what the label can't show
/// itself (a truncated value, the address behind a name).
const RelationAndSharer: React.FC<{
  relation: string;
  label: string;
  tooltip?: string;
}> = ({ relation, label, tooltip }) => {
  if (!label) return <>{relation}</>;
  const sharer = (
    <span className="inline-block truncate" style={{ maxWidth: "12rem" }}>
      {label}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1">
      <span className="shrink-0">{relation} ·</span>
      {tooltip ? (
        <Tooltip title={tooltip} placement="top">
          {sharer}
        </Tooltip>
      ) : (
        sharer
      )}
    </span>
  );
};

/// An expander, not a link: a foreign org's space has no page of ours.
const SharedSpaceGroup: React.FC<{
  space: SharedSpace;
  models: ModelElementType[];
}> = ({ space, models }) => {
  const [expanded, setExpanded] = useState(true);
  const dotColor = resolveDatasetColor(
    spaceColor({ space_id: space.space_id, color: space.color }),
  );
  const granter = space.granted_by_name
    ? space.granted_by_email
      ? `${space.granted_by_name} (${space.granted_by_email})`
      : space.granted_by_name
    : space.granted_by_email;

  return (
    <div className="mb-1">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        data-testid={`shared-space-${space.space_id}`}
        className="flex items-center gap-2 item-height px-1 rounded-app cursor-pointer hover:bg-list-hover"
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          setExpanded((v) => !v);
        }}
      >
        <span className="flex w-5 shrink-0 items-center justify-center text-fg-muted">
          {expanded ? (
            <ExpandMoreIcon sx={{ fontSize: 14 }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 14 }} />
          )}
        </span>
        <span
          className="w-2 h-2 shrink-0 rounded-app"
          style={{ background: dotColor }}
        />
        <span className="text-fg-medium truncate">{space.name}</span>
        {space.sealed && (
          <StatusChip>
            <span className="inline-flex items-center gap-1">
              <LockOutlinedIcon sx={{ fontSize: 10 }} />
              Sealed
            </span>
          </StatusChip>
        )}
        <span className="text-fg-muted-regular-small shrink-0">
          <RelationAndSharer
            relation={roleLabel(space.role)}
            label={space.org_label}
            tooltip={granter ? `Shared by ${granter}` : undefined}
          />
        </span>
        <span className="flex-1" />
        <span className="text-fg-muted-regular-small shrink-0">
          {space.model_count === 1 ? "1 model" : `${space.model_count} models`}
        </span>
      </div>
      {expanded && (
        <div className="ml-5">
          {models.length === 0 ? (
            <div className="flex items-center gap-2 item-height px-1 text-small text-fg-muted italic">
              Models in this space appear here as they load.
            </div>
          ) : (
            models.map((model) => (
              <ModelRow
                key={model.model_id}
                model={model}
                sub={
                  model.relation === "Owner"
                    ? "via space"
                    : (model.relation ?? "via space")
                }
                meta={
                  model.last_opened_by_me != null
                    ? formatCompactRelativeTime(model.last_opened_by_me)
                    : undefined
                }
              />
            ))
          )}
          {space.sealed && (
            <div className="px-1 pb-2 text-small text-fg-muted italic">
              Sealed: you can read it in the app. No export, no clipboard, no
              download, no agent access, and nothing may be imported out of it —
              for everyone, its managers included.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/// The owner is who shared it: their name with the address on hover, or the
/// address alone when they have no name.
const StandaloneSub: React.FC<{ model: ModelElementType }> = ({ model }) => {
  const email = model.owner_id || "";
  const name = model.owner_name?.trim();
  return (
    <RelationAndSharer
      relation={model.relation || "Shared"}
      label={name || email}
      tooltip={name ? email : undefined}
    />
  );
};

/// Shares from orgs the caller is NOT a member of (D32). The org decides
/// placement, never the grant's shape: same-org shares stay on the Overview.
export const SharedWithMePage: React.FC = () => {
  const { isOnline, modelsInitialized, updateModelList } = useModelsPolling();
  const availableModels = useMainStateStore((state) => state.availableModels);
  const { spaces, status, refresh } = useSharedWithMeStore(
    useShallow((s) => ({
      spaces: s.spaces,
      status: s.status,
      refresh: s.refresh,
    })),
  );

  // An FGA ListObjects per call, so this page is the only place it polls.
  useEffect(() => {
    if (navigator.onLine) void refresh();
    const tick = window.setInterval(() => {
      if (navigator.onLine && pollingAllowed()) void refresh();
    }, SPACES_POLL_INTERVAL_MS);
    return () => clearInterval(tick);
  }, [refresh]);

  const [refreshing, setRefreshing] = useState(false);
  const refreshBothHalves = async (): Promise<void> => {
    setRefreshing(true);
    try {
      await Promise.all([
        refresh(),
        // `force`: a tick already in flight carries pre-share data.
        updateModelList({ showLoading: false, force: true }),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const { bySpace, standalone } = useMemo(() => {
    const external = selectExternalModels(availableModels);
    const granted = new Set(spaces.map((sp) => sp.space_id));
    return partitionSharedModels(external, granted);
  }, [availableModels, spaces]);

  // Offline never reaches the fetch, so it would otherwise sit on the spinner
  // forever — say so instead, the way the Overview does.
  const offline = !isOnline && status !== "ready";
  // An unreachable endpoint is not an empty page: saying "nothing is shared"
  // here would state the opposite of what we know.
  if (status === "error" && !offline) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <EmptyState
          title="Couldn't load what's shared with you"
          subtitle="The list will reappear on its own once the connection recovers."
        />
      </div>
    );
  }
  const isEmpty = spaces.length + standalone.length === 0;

  return (
    <div
      className="relative flex flex-col items-center w-full h-full overflow-y-auto px-6"
      style={{
        opacity: isOnline ? 1 : 0.55,
        transition: "opacity var(--duration-short) var(--ease-move)",
      }}
    >
      <ColumnTopSpacer />
      <div
        className="w-full flex flex-col"
        style={{
          maxWidth: "45rem",
          paddingBottom: "2.5rem",
        }}
      >
        <div className="flex items-center gap-3">
          <h1 className="text-fg-title leading-none">Shared with me</h1>
          <span className="flex-1" />
          {/* Both halves poll; this skips waiting out the tick. */}
          <TooltipButton
            title="Refresh"
            aria-label="Refresh shared items"
            data-testid="refresh-shared-with-me"
            variant="default"
            size="xs"
            disabled={refreshing}
            onClick={() => void refreshBothHalves()}
          >
            <RefreshIcon className={refreshing ? "animate-spin" : undefined} />
          </TooltipButton>
        </div>
        <div className="mt-2 mb-8 flex items-start gap-1.5 text-fg-muted-regular">
          <InfoOutlinedIcon sx={{ fontSize: 13 }} className="shrink-0 mt-px" />
          <span>
            From organizations you are <b className="text-fg-medium">not</b> a
            member of. Everything inside your own organizations stays in Spaces.
            Nothing here counts against your quota.
          </span>
        </div>

        {offline ? (
          <HintRow>
            You&apos;re offline — anything shared with you will appear when the
            connection restores
          </HintRow>
        ) : !modelsInitialized || status === "loading" ? (
          <HintRow loading>Loading shared items…</HintRow>
        ) : isEmpty ? (
          <EmptyState
            title="Nothing is shared with you from outside your organizations"
            subtitle="When someone in another organization shares a model or grants you a space, it shows up here."
          />
        ) : (
          <>
            {spaces.length > 0 && (
              <div className="mb-6" data-testid="shared-spaces-section">
                <SectionRule
                  inset
                  icon={<FolderSharedOutlinedIcon sx={{ fontSize: 14 }} />}
                  label="Spaces shared with you"
                  trailing={String(spaces.length)}
                />
                {spaces.map((space) => (
                  <SharedSpaceGroup
                    key={space.space_id}
                    space={space}
                    models={bySpace.get(space.space_id) ?? []}
                  />
                ))}
              </div>
            )}

            {standalone.length > 0 && (
              <div className="mb-6" data-testid="shared-models-section">
                <SectionRule
                  inset
                  icon={<DashboardOutlinedIcon sx={{ fontSize: 14 }} />}
                  label="Models shared with you"
                  trailing={String(standalone.length)}
                />
                {standalone.map((model) => (
                  <ModelRow
                    key={model.model_id}
                    model={model}
                    sub={<StandaloneSub model={model} />}
                    meta={
                      model.last_opened_by_me != null
                        ? formatCompactRelativeTime(model.last_opened_by_me)
                        : undefined
                    }
                  />
                ))}
              </div>
            )}

            {/* Same grant shape, different home — not duplicated here. */}
            <div className="text-small text-fg-muted italic">
              Shared tables surface inside the model you import them into and in
              Search; shared datasets live on the Datasets page.
            </div>
          </>
        )}
      </div>
    </div>
  );
};
